import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { CameraView } from '../components/CameraView';
import { StatCard } from '../components/StatCard';
import { Button } from '../components/Button';
import { ExerciseType, RepContext, WorkoutSession, WorkoutMode } from '../types';
import { EXERCISE_CONFIG } from '../constants';
import { useAuth } from '../contexts/AuthContext';
import { saveWorkoutToHistory } from '../services/database';
import { generateWorkoutSticker, downloadSticker } from '../services/share';
import { Share, Download, X, RefreshCcw } from 'lucide-react';

const INITIAL_CONTEXT: RepContext = {
  count: 0,
  state: 'UP' as any,
  feedback: 'Ready',
  lastRepTime: 0,
  formScore: 100
};

export const Workout: React.FC = () => {
  const { type } = useParams<{ type: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  
  // Extract Parameters
  const exerciseType = (type as ExerciseType) || ExerciseType.SQUAT;
  const mode = (searchParams.get('mode') as WorkoutMode) || WorkoutMode.FREE;
  const targetDuration = parseInt(searchParams.get('duration') || '0', 10);

  const [context, setContext] = useState<RepContext>({ ...INITIAL_CONTEXT });
  const contextRef = useRef<RepContext>(INITIAL_CONTEXT); 

  const [setStartTime, setSetStartTime] = useState<number>(Date.now());
  
  // Prep / Countdown State
  const [isPrep, setIsPrep] = useState(mode === WorkoutMode.TIME_ATTACK);
  const [prepTimeLeft, setPrepTimeLeft] = useState(10);
  const [startRepOffset, setStartRepOffset] = useState(0);

  // Timer State
  const [timeLeft, setTimeLeft] = useState<number>(targetDuration);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  
  // UI State
  const [isSaving, setIsSaving] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [hasFinishedTimeAttack, setHasFinishedTimeAttack] = useState(false);
  
  // Share State
  const [stickerUrl, setStickerUrl] = useState<string | null>(null);
  const [isGeneratingSticker, setIsGeneratingSticker] = useState(false);

  // Session-wide tracking for multi-set workouts
  const [totalSessionReps, setTotalSessionReps] = useState(0);
  const [totalSessionDuration, setTotalSessionDuration] = useState(0);
  const [sessionAccuracyScores, setSessionAccuracyScores] = useState<number[]>([]);

  const timerRef = useRef<any>(null);

  const handleUpdate = useCallback((newContext: RepContext) => {
    contextRef.current = newContext; 
    if (!isSummaryOpen && !hasFinishedTimeAttack) {
      setContext(newContext);
    }
  }, [isSummaryOpen, hasFinishedTimeAttack]);

  // TIMER LOGIC
  useEffect(() => {
    if (isSummaryOpen || hasFinishedTimeAttack) return;

    timerRef.current = setInterval(() => {
      if (isPrep) {
        setPrepTimeLeft((prev) => {
          if (prev <= 1) {
            setIsPrep(false);
            setStartRepOffset(contextRef.current.count); 
            setSetStartTime(Date.now());
            return 0;
          }
          return prev - 1;
        });
      } else {
        if (mode === WorkoutMode.TIME_ATTACK) {
          setTimeLeft((prev) => {
            if (prev <= 1) {
              clearInterval(timerRef.current);
              setHasFinishedTimeAttack(true);
              setIsSummaryOpen(true);
              return 0;
            }
            return prev - 1;
          });
        } else {
          setElapsedTime((prev) => prev + 1);
        }
      }
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [mode, isSummaryOpen, hasFinishedTimeAttack, isPrep]);


  const saveLocal = (session: WorkoutSession) => {
    try {
      const existing = JSON.parse(localStorage.getItem('workouts') || '[]');
      localStorage.setItem('workouts', JSON.stringify([session, ...existing]));
    } catch (e) {
      console.error("Failed to save locally", e);
    }
  };

  const getCurrentSessionObject = (): WorkoutSession => {
    let finalDuration = 0;
    if (mode === WorkoutMode.TIME_ATTACK) {
      finalDuration = targetDuration - timeLeft; 
    } else {
      finalDuration = Math.floor((Date.now() - setStartTime) / 1000);
    }
    
    const currentSetReps = Math.max(0, context.count - startRepOffset);

    return {
      id: Date.now().toString(),
      date: new Date().toISOString(),
      exercise: exerciseType,
      mode: mode,
      targetDuration: targetDuration > 0 ? targetDuration : undefined,
      reps: currentSetReps,
      durationSeconds: finalDuration,
      accuracyScore: context.formScore,
    };
  }

  const saveCurrentSet = async (): Promise<boolean> => {
    setIsSaving(true);
    const session = getCurrentSessionObject();

    if (session.reps === 0 && mode === WorkoutMode.FREE) {
      setIsSaving(false);
      return true; 
    }

    try {
      // Check if user exists AND is not the guest user
      if (user && user.uid !== 'guest') {
        try {
          await saveWorkoutToHistory(user.uid, session, profile);
        } catch (firebaseError) {
          console.warn("Firebase save failed, falling back to local", firebaseError);
          saveLocal(session);
        }
      } else {
        // Guest user or offline
        saveLocal(session);
      }
      return true;
    } catch (error) {
      console.error("Critical error saving workout", error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleCompletePress = () => {
    setIsSummaryOpen(true);
  };

  const handleNextSet = async () => {
    const session = getCurrentSessionObject();
    const success = await saveCurrentSet();
    if (success) {
      // Accumulate stats for the session
      setTotalSessionReps(prev => prev + session.reps);
      setTotalSessionDuration(prev => prev + session.durationSeconds);
      setSessionAccuracyScores(prev => [...prev, session.accuracyScore]);

      // Reset for next set
      setSetStartTime(Date.now());
      setElapsedTime(0);
      setTimeLeft(targetDuration);
      setHasFinishedTimeAttack(false);
      setStickerUrl(null);
      
      if (mode === WorkoutMode.TIME_ATTACK) {
        setIsPrep(true);
        setPrepTimeLeft(10);
      } else {
        setIsPrep(false);
        setStartRepOffset(contextRef.current.count);
      }

      setIsSummaryOpen(false);
    }
  };

  const handleFinishWorkout = async () => {
    await saveCurrentSet();
    navigate('/history');
  };

  const handleShare = async () => {
    setIsGeneratingSticker(true);
    const currentSetSession = getCurrentSessionObject();
    try {
        let shareSession: WorkoutSession;

        if (mode === WorkoutMode.FREE) {
            const finalReps = totalSessionReps + currentSetSession.reps;
            const finalDuration = totalSessionDuration + currentSetSession.durationSeconds;
            const allScores = [...sessionAccuracyScores, currentSetSession.accuracyScore];
            const finalAccuracy = allScores.length > 0
                ? Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length)
                : currentSetSession.accuracyScore;

            shareSession = {
                ...currentSetSession,
                reps: finalReps,
                durationSeconds: finalDuration,
                accuracyScore: finalAccuracy,
            };
        } else {
            // For time attack, behavior remains the same.
            shareSession = currentSetSession;
        }

        const url = await generateWorkoutSticker(shareSession);
        setStickerUrl(url);
    } catch (e) {
        console.error("Sticker generation failed", e);
        alert("Could not generate export image. Please try again.");
    } finally {
        setIsGeneratingSticker(false);
    }
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const displayReps = Math.max(0, context.count - startRepOffset);

  return (
    <div className="relative w-screen h-[100dvh] bg-black overflow-hidden flex flex-col">
      
      <div className="absolute inset-0 z-0">
        <CameraView 
          exerciseType={exerciseType} 
          onUpdate={handleUpdate} 
          isPaused={isSummaryOpen || hasFinishedTimeAttack}
          className="w-full h-full"
        />
      </div>

      <div className="absolute top-0 left-0 right-0 z-10 p-4 bg-gradient-to-b from-black/80 to-transparent pointer-events-none">
        <div className="grid grid-cols-3 gap-4">
          <div className="flex flex-col gap-2">
             <StatCard label="Reps" value={displayReps} color="text-white" />
          </div>
          <div className="flex flex-col gap-2">
             {mode === WorkoutMode.TIME_ATTACK ? (
                <StatCard 
                  label={isPrep ? "Get Ready" : "Time Left"}
                  value={isPrep ? `0:${prepTimeLeft.toString().padStart(2, '0')}` : formatTime(timeLeft)} 
                  color={!isPrep && timeLeft < 10 ? "text-red-500 animate-pulse" : "text-white"} 
                />
             ) : (
                <StatCard label="Time" value={formatTime(elapsedTime)} color="text-white" />
             )}
          </div>
          <div className="bg-surface/90 backdrop-blur-md border border-white/20 rounded-sm p-4 flex flex-col justify-center items-center text-center shadow-lg">
             <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold font-mono">Feedback</span>
             <span className="text-lg font-bold text-white mt-1 uppercase tracking-tight leading-tight">{context.feedback}</span>
          </div>
        </div>
      </div>

      {isPrep && (
          <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
             <div className="text-center p-10 border border-white/10 rounded-2xl bg-black/40">
                <h2 className="text-4xl font-black text-white italic uppercase tracking-tighter mb-6 animate-pulse">Get Ready</h2>
                <div className="relative inline-block">
                  <span className="text-[120px] font-black text-white leading-none drop-shadow-[0_0_30px_rgba(255,255,255,0.5)]">
                    {prepTimeLeft}
                  </span>
                </div>
                <p className="text-slate-300 font-mono text-sm uppercase tracking-widest mt-8">Position yourself in frame</p>
             </div>
          </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 z-20 p-4 pb-[calc(20px+env(safe-area-inset-bottom))] bg-gradient-to-t from-black/90 via-black/50 to-transparent pointer-events-auto">
        <div className="flex gap-4 max-w-md mx-auto w-full">
            <Button 
                variant="secondary" 
                fullWidth 
                onClick={() => navigate('/')} 
                disabled={isSaving || isSummaryOpen}
            >
                Abort
            </Button>
            <Button 
                variant="primary" 
                fullWidth 
                onClick={handleCompletePress} 
                disabled={isSaving || isSummaryOpen || isPrep}
            >
                {mode === WorkoutMode.TIME_ATTACK ? 'End' : 'Finish'}
            </Button>
        </div>
      </div>

      {isSummaryOpen && (
          <div className="absolute inset-0 z-50 bg-black/95 backdrop-blur-lg flex items-center justify-center p-6 overflow-y-auto">
            {stickerUrl ? (
              <div className="w-full max-w-sm flex flex-col gap-4 animate-in fade-in zoom-in duration-300">
                 <div className="flex justify-between items-center">
                    <h3 className="text-white font-bold uppercase tracking-wide flex items-center gap-2">
                      <Share className="w-4 h-4 text-accent" />
                      Ready to Share
                    </h3>
                    <button onClick={() => setStickerUrl(null)} className="p-2 bg-white/10 rounded-full hover:bg-white/20 text-white transition-colors">
                      <X className="w-5 h-5" />
                    </button>
                 </div>
                 
                 <div className="relative group">
                    <img 
                      src={stickerUrl} 
                      alt="Export Preview" 
                      className="w-full rounded-sm border border-white/10 shadow-[0_0_30px_rgba(0,0,0,0.5)]" 
                    />
                    <div className="absolute bottom-2 right-2 bg-black/50 px-2 py-1 rounded text-[10px] text-white/70 font-mono uppercase">
                      1080 x 1350
                    </div>
                 </div>

                 <div className="grid grid-cols-2 gap-3">
                    <Button variant="secondary" fullWidth onClick={() => setStickerUrl(null)}>
                      Back
                    </Button>
                    <Button 
                      variant="primary" 
                      fullWidth 
                      onClick={() => downloadSticker(stickerUrl, `levelup-${Date.now()}.png`)} 
                      icon={<Download className="w-4 h-4" />}
                    >
                      Save Image
                    </Button>
                 </div>
              </div>
            ) : (
              <div className="w-full max-w-sm bg-surface border border-white/20 p-8 text-center relative overflow-hidden animate-in fade-in zoom-in duration-300 shadow-2xl">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white to-transparent opacity-50"></div>
                
                <h2 className="text-3xl font-black text-white uppercase tracking-tighter mb-2">
                  {hasFinishedTimeAttack ? "TIME'S UP!" : "Set Complete"}
                </h2>
                <p className="text-slate-400 font-mono text-xs uppercase tracking-widest mb-8">Performance Data</p>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-black/50 p-4 border border-white/10">
                    <span className="block text-xs text-slate-500 font-mono uppercase">Reps</span>
                    <span className="text-3xl font-bold text-white">{displayReps}</span>
                  </div>
                  <div className="bg-black/50 p-4 border border-white/10">
                    <span className="block text-xs text-slate-500 font-mono uppercase">Accuracy</span>
                    <span className={`text-3xl font-bold ${context.formScore >= 80 ? 'text-green-400' : 'text-amber-400'}`}>
                      {context.formScore}%
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                  <Button 
                    fullWidth 
                    variant="secondary" 
                    onClick={handleShare} 
                    disabled={isGeneratingSticker}
                    icon={isGeneratingSticker ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <Share className="w-4 h-4" />}
                  >
                    {isGeneratingSticker ? 'Designing...' : 'Share Result'}
                  </Button>

                  <div className="h-px bg-white/10 my-2"></div>

                  <Button fullWidth onClick={handleNextSet} disabled={isSaving} variant="primary">
                    {isSaving ? 'Saving...' : 'Start Next Set'}
                  </Button>
                  <Button fullWidth variant="ghost" onClick={handleFinishWorkout} disabled={isSaving}>
                    Finish Workout
                  </Button>
                </div>
              </div>
            )}
          </div>
      )}

    </div>
  );
};
